import { useEffect, useRef, useState } from 'react';
import { api, SiteAssetKey } from '../../services/api';
import { useImageUpload } from '../../hooks/useImageUpload';
import { invalidateSiteAssetsCache, resolveAssetUrl } from '../../hooks/useSiteAssets';
import { toast } from '../../hooks/use-toast';
import { Icon } from '../icons/Icon';

/**
 * SiteAssetManagement — サイトアセット管理（Admin）
 *
 * ロゴ・ヒーロー背景・各街区背景を登録/差し替え/解除する。
 * アップロード → R2保存（既存 /api/upload/image・マジックナンバー検証つき）
 * → PUT /api/admin/site-assets/:key で D1 に登録。
 * 未登録スロットは本番で既定デザイン（グラデーション）表示＝正常系。
 */

const SLOTS: { key: SiteAssetKey; label: string; hint: string }[] = [
    { key: 'logo', label: 'ヘッダーロゴ', hint: '正方形推奨（例: 256×256）。未登録時は3役色の既定マーク' },
    { key: 'heroBg', label: 'ヒーロー背景（街の門）', hint: '1600×600推奨・文字が乗るため薄めの画像' },
    { key: 'salonBg', label: '文壇サロン 街区背景', hint: '1200×600推奨・薄めの画像（琥珀のグロー既定）' },
    { key: 'tokiwaBg', label: 'トキワ荘 街区背景', hint: '1200×600推奨・薄めの画像（紫のグロー既定）' },
    { key: 'stageBg', label: '声劇ステージ 街区背景', hint: '1200×600推奨・薄めの画像（ティールのグロー既定）' },
];

type AssetRow = { key: SiteAssetKey; url: string; file_key: string | null; updated_at: number };

export const SiteAssetManagement = () => {
    const [assets, setAssets] = useState<Partial<Record<SiteAssetKey, AssetRow>>>({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [busyKey, setBusyKey] = useState<SiteAssetKey | null>(null);
    const [confirmKey, setConfirmKey] = useState<SiteAssetKey | null>(null);
    const { uploadImage } = useImageUpload();
    const fileInputRefs = useRef<Partial<Record<SiteAssetKey, HTMLInputElement | null>>>({});

    const loadAssets = async () => {
        setLoading(true);
        setLoadError(false);
        const res = await api.adminListSiteAssets();
        if (res.success && res.data) {
            const map: Partial<Record<SiteAssetKey, AssetRow>> = {};
            for (const row of res.data) map[row.key] = row;
            setAssets(map);
        } else {
            setLoadError(true);
        }
        setLoading(false);
    };

    useEffect(() => { loadAssets(); }, []);

    const handleUpload = async (key: SiteAssetKey, file: File) => {
        setBusyKey(key);
        try {
            const up = await uploadImage(file);
            if (!up.success || !up.data) {
                toast({ title: 'アップロードに失敗しました', description: up.error, variant: 'destructive' });
                return;
            }
            const put = await api.adminPutSiteAsset(key, { url: up.data.url, file_key: up.data.key });
            if (!put.success) {
                toast({ title: '登録に失敗しました', description: put.error, variant: 'destructive' });
                return;
            }
            invalidateSiteAssetsCache();
            toast({ title: '画像を登録しました', description: 'サイト全体に反映されます（最大1分キャッシュ）。' });
            await loadAssets();
        } finally {
            setBusyKey(null);
            const input = fileInputRefs.current[key];
            if (input) input.value = '';
        }
    };

    const handleRemove = async (key: SiteAssetKey) => {
        setConfirmKey(null);
        setBusyKey(key);
        try {
            const res = await api.adminDeleteSiteAsset(key);
            if (!res.success) {
                toast({ title: '解除に失敗しました', description: res.error, variant: 'destructive' });
                return;
            }
            invalidateSiteAssetsCache();
            toast({ title: '画像を手放しました', description: '既定デザインに戻ります。' });
            await loadAssets();
        } finally {
            setBusyKey(null);
        }
    };

    if (loading) {
        return <div className="text-ink-2 p-4" aria-busy="true">読み込み中...</div>;
    }

    if (loadError) {
        return (
            <div className="pop-card p-6 text-center" role="alert">
                <p className="font-bold text-ink mb-2">サイトアセットを読み込めませんでした</p>
                <button className="btn btn-primary btn-sm" onClick={loadAssets}>
                    <Icon name="repeat" size={14} /> もう一度ためす
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-xl font-display font-bold text-ink">サイト画像管理</h2>
                <p className="text-sm text-ink-2 mt-1">
                    ロゴ・ヒーロー背景・街区背景を登録できます。未登録のスロットは既定デザイン
                    （グラデーション）で表示されます（フェイルセーフ）。対応形式: JPEG / PNG / WebP / GIF・5MBまで。
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SLOTS.map(slot => {
                    const current = assets[slot.key];
                    const busy = busyKey === slot.key;
                    return (
                        <div key={slot.key} className="pop-card p-4">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <h3 className="font-display font-bold text-ink">{slot.label}</h3>
                                    <p className="text-[12px] text-ink-2 mt-0.5">{slot.hint}</p>
                                </div>
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${current ? 'bg-role-voice-soft text-role-voice-text border-transparent' : 'bg-cream text-ink-2 border-ink/20'}`}>
                                    {current ? '登録済み' : '未登録（既定デザイン）'}
                                </span>
                            </div>

                            <div className="mt-3 h-28 rounded-pop-sm border-2 border-ink/15 overflow-hidden bg-cream flex items-center justify-center">
                                {current ? (
                                    <img
                                        src={`${resolveAssetUrl(current.url)}${current.url.includes('?') ? '&' : '?'}v=${current.updated_at}`}
                                        alt={`${slot.label} の現在の画像`}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-[12px] text-ink-2">プレビューなし — 既定デザインで表示中</span>
                                )}
                            </div>

                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <input
                                    ref={el => { fileInputRefs.current[slot.key] = el; }}
                                    id={`site-asset-${slot.key}`}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="sr-only peer"
                                    disabled={busy}
                                    onChange={e => {
                                        const file = e.target.files?.[0];
                                        if (file) handleUpload(slot.key, file);
                                    }}
                                />
                                <label
                                    htmlFor={`site-asset-${slot.key}`}
                                    className={`btn btn-primary btn-sm peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 ${busy ? 'opacity-50 pointer-events-none' : ''}`}
                                >
                                    <Icon name="plus" size={14} /> {busy ? '登録中…' : current ? '画像を差し替える' : '画像を登録する'}
                                </label>

                                {current && confirmKey !== slot.key && (
                                    <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirmKey(slot.key)}>
                                        既定デザインに戻す
                                    </button>
                                )}
                                {/* 破壊的操作の確認（文言変換表: YES/NO禁止） */}
                                {current && confirmKey === slot.key && (
                                    <span className="flex items-center gap-2" role="group" aria-label={`${slot.label} の画像を解除する確認`}>
                                        <button className="btn btn-pink btn-sm" disabled={busy} onClick={() => handleRemove(slot.key)}>
                                            手放す
                                        </button>
                                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmKey(null)}>
                                            やっぱりやめる
                                        </button>
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
